import { useCallback } from "react";
import { slugify } from "@tellescope/utils";
import type { PortalSchema } from "../../../shared/schema";
import { useAuth } from "@tellescope/tanstack-query";

// Domains whose users manage Tellescope instances on behalf of customers (Tellescope staff
// and OpenLoop, a partner that administers many subaccounts). Exports for these users strip
// integration credentials so they aren't accidentally carried into customer templates.
const TEMPLATE_ADMIN_DOMAINS = ["tellescope.com", "openloophealth.com"];

const isTemplateAdmin = (email: string | undefined): boolean => {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return TEMPLATE_ADMIN_DOMAINS.some((d) => domain === d);
};

export function useSchemaExport() {
  const { currentUser } = useAuth();

  const exportToFile = useCallback(
    (schema: PortalSchema) => {
      // Exclude all integration configs for internal users — they contain customer-specific credentials
      let exportSchema: object = schema;
      if (isTemplateAdmin(currentUser?.email)) {
        const { settings: { integrations: _integrations, ...restSettings } = {}, ...restSchema } = schema;
        exportSchema = { ...restSchema, settings: restSettings };
      }

      // Create JSON string with pretty formatting
      const jsonString = JSON.stringify(exportSchema, null, 2);

      // Create blob
      const blob = new Blob([jsonString], { type: "application/json" });

      // Create download URL
      const url = URL.createObjectURL(blob);

      // Create temporary anchor element
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${slugify(schema.name)}-portal-schema.json`;

      // Trigger download
      document.body.appendChild(anchor);
      anchor.click();

      // Cleanup
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    [currentUser]
  );

  return { exportToFile };
}
